import {
  Box,
  Chip,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PendingIcon from "@mui/icons-material/PendingOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useEffect, useState } from "react";
import { IconColorPicker } from "../../components/IconColorPicker";
import type {
  Category,
  ImportEntityPreviewRow,
  ImportEntityUpdatePayload,
  Member,
} from "../../lib/types";
import { isEditableRowStatus } from "./importHelpers";
export type EntityPreviewListProps = {
  title: string;
  rows: ImportEntityPreviewRow[];
  kind: "categories" | "accounts";
  categories: Category[];
  members: Member[];
  isUpdating: boolean;
  isBatchApplied: boolean;
  onUpdate: (entityId: string, payload: ImportEntityUpdatePayload) => void;
};

export function EntityPreviewList({ title, rows, kind, categories, members, isUpdating, isBatchApplied, onUpdate }: EntityPreviewListProps) {
  const categoryGroups = kind === "categories" ? groupedCategoryEntityRows(rows) : [];
  return (
    <Box className="import-entity-preview">
      <h3>{title}</h3>
      {!rows.length ? <p className="status">No {title.toLowerCase()} in this import.</p> : null}
      {kind === "categories"
        ? categoryGroups.map((group) => {
            const parent = group.parent;
            return (
              <Box className="import-entity-category-group" key={parent?.id || parent?.entity_id || parent?.name || group.children.map((row) => row.id || row.name).join("-")}>
                {parent ? (
                  <ImportEntityEditor
                    row={parent}
                    kind={kind}
                    categories={categories}
                    members={members}
                    isUpdating={isUpdating}
                    isBatchApplied={isBatchApplied}
                    onUpdate={onUpdate}
                  />
                ) : (
                  <Box className="import-entity-group-heading">Parent: {group.children[0]?.parent_name || "from file"}</Box>
                )}
                {group.children.length ? (
                  <Box className="import-entity-children">
                    {group.children.map((row) => (
                      <ImportEntityEditor
                        key={row.id || `${row.row_index}-${row.name}`}
                        row={row}
                        kind={kind}
                        categories={categories}
                        members={members}
                        isUpdating={isUpdating}
                        isBatchApplied={isBatchApplied}
                        onUpdate={onUpdate}
                      />
                    ))}
                  </Box>
                ) : null}
              </Box>
            );
          })
        : rows.map((row) => (
            <ImportEntityEditor
              key={row.id || `${row.row_index}-${row.name}`}
              row={row}
              kind={kind}
              categories={categories}
              members={members}
              isUpdating={isUpdating}
              isBatchApplied={isBatchApplied}
              onUpdate={onUpdate}
            />
          ))}
    </Box>
  );
}

export function CurrencyPreviewList({ rows }: { rows: ImportEntityPreviewRow[] }) {
  const statusLabel = (status?: string | null) => {
    const normalized = String(status || "mapped").toLowerCase();
    if (normalized === "imported") return "Imported";
    if (normalized === "mapped" || normalized === "confirmed") return "Already added";
    if (normalized === "pending") return "Will be added";
    return normalized.replace("_", " ");
  };
  return (
    <Box className="import-entity-preview">
      <h3>Currencies</h3>
      {!rows.length ? <p className="status">No currencies in this import.</p> : null}
      {rows.map((row) => (
        <Box className="import-currency-preview-row" key={row.id || `${row.row_index}-${row.name}`}>
          <span className="currency-icon" aria-hidden="true">{row.icon || row.name}</span>
          <Box className="currency-settings-copy">
            <strong>{row.name}</strong>
            <span>{row.type || row.name}</span>
          </Box>
          <Chip size="small" label={statusLabel(row.status)} variant="outlined" />
        </Box>
      ))}
    </Box>
  );
}

export function ImportEntityEditor({
  row,
  kind,
  categories,
  members,
  isUpdating,
  isBatchApplied,
  onUpdate,
}: {
  row: ImportEntityPreviewRow;
  kind: "categories" | "accounts";
  categories: Category[];
  members: Member[];
  isUpdating: boolean;
  isBatchApplied: boolean;
  onUpdate: (entityId: string, payload: ImportEntityUpdatePayload) => void;
}) {
  const [name, setName] = useState(row.name || "");
  const [type, setType] = useState(row.type || (kind === "accounts" ? "bank" : "expense"));
  const [icon, setIcon] = useState(row.icon || (kind === "accounts" ? "wallet" : "tag"));
  const [color, setColor] = useState(row.color || "#2563eb");
  const [ownerUserId, setOwnerUserId] = useState(row.owner_user_id || "");
  const [categoryKind, setCategoryKind] = useState(row.category_kind || "category");
  const [parentId, setParentId] = useState(row.parent_id || "");
  const [status, setStatus] = useState(entityStatusValue(row.status));
  const locked = isUpdating || isBatchApplied || !row.id;

  useEffect(() => {
    setName(row.name || "");
    setType(row.type || (kind === "accounts" ? "bank" : "expense"));
    setIcon(row.icon || (kind === "accounts" ? "wallet" : "tag"));
    setColor(row.color || "#2563eb");
    setOwnerUserId(row.owner_user_id || "");
    setCategoryKind(row.category_kind || "category");
    setParentId(row.parent_id || "");
    setStatus(entityStatusValue(row.status));
  }, [kind, row]);

  const update = (payload: ImportEntityUpdatePayload) => {
    if (!row.id) return;
    onUpdate(row.id, payload);
  };
  const parentOptions = categories.filter((category) => category.type === type && !category.parent_id && category.id !== row.entity_id && !category.is_archived);

  return (
    <Box className={`import-entity-row import-entity-${kind}${row.category_kind === "subcategory" ? " subcategory" : ""}`}>
      <Box className="import-entity-fields">
        <IconColorPicker
          className="import-entity-picker"
          color={color}
          colorAriaLabel={kind === "accounts" ? "Choose account color" : "Choose category color"}
          disabled={locked}
          icon={icon}
          iconAriaLabel={kind === "accounts" ? "Choose account icon" : "Choose category icon"}
          iconDataAttribute={kind === "accounts" ? "account" : "category"}
          pickerId={`importEntityIconPicker-${row.id || row.row_index}`}
          pickerLabel={kind === "accounts" ? "Account icon" : "Category icon"}
          onColorChange={(nextColor) => {
            setColor(nextColor);
            update({ color: nextColor });
          }}
          onIconChange={(nextIcon) => {
            setIcon(nextIcon);
            update({ icon: nextIcon });
          }}
        />
        <TextField
          size="small"
          label="Name"
          value={name}
          disabled={locked}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => update({ name: name.trim() || row.name })}
        />
        <TextField
          select
          size="small"
          label="Type"
          value={type}
          disabled={locked}
          onChange={(event) => {
            const next = event.target.value;
            setType(next);
            setParentId("");
            update({ type: next, parent_id: null });
          }}
        >
          {(kind === "accounts" ? ["cash", "bank", "card", "savings", "debt"] : ["expense", "income"]).map((value) => (
            <MenuItem key={value} value={value}>
              {value.replace(/^\w/, (letter) => letter.toUpperCase())}
            </MenuItem>
          ))}
        </TextField>
        {kind === "accounts" ? (
          <TextField
            select
            size="small"
            label="Mapped user"
            value={ownerUserId}
            disabled={locked}
            onChange={(event) => {
              const next = event.target.value;
              setOwnerUserId(next);
              update({ owner_user_id: next || null });
            }}
          >
            <MenuItem value="">No mapped user</MenuItem>
            {members.map((member) => (
              <MenuItem key={member.user_id} value={member.user_id}>
                {member.display_name}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <>
            <TextField
              select
              size="small"
              label="Kind"
              value={categoryKind}
              disabled={locked}
              onChange={(event) => {
                const next = event.target.value;
                setCategoryKind(next);
                if (next === "category") setParentId("");
                update({ category_kind: next, parent_id: next === "category" ? null : parentId || null });
              }}
            >
              <MenuItem value="category">Category</MenuItem>
              <MenuItem value="subcategory">Subcategory</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Parent"
              value={parentId}
              disabled={locked || categoryKind !== "subcategory"}
              onChange={(event) => {
                const next = event.target.value;
                setParentId(next);
                update({ parent_id: next || null, category_kind: next ? "subcategory" : "category" });
              }}
            >
              <MenuItem value="">No parent</MenuItem>
              {parentOptions.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
        <ImportStatusToggle
          value={status}
          disabled={locked}
          ariaLabel="Import entity status"
          onChange={(next) => {
            setStatus(next);
            update({ status: next });
          }}
        />
      </Box>
      {row.error ? <p className="import-row-message error">{row.error}</p> : null}
    </Box>
  );
}

export function ImportStatusToggle({
  value,
  disabled,
  ariaLabel,
  onChange,
}: {
  value: string;
  disabled: boolean;
  ariaLabel: string;
  onChange: (value: "confirmed" | "pending" | "ignored") => void;
}) {
  return (
    <ToggleButtonGroup
      className="import-status-toggle"
      size="small"
      exclusive
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(_, nextValue) => {
        const next = String(nextValue || "");
        if (!isEditableRowStatus(next)) return;
        onChange(next as "confirmed" | "pending" | "ignored");
      }}
    >
      <ToggleButton value="confirmed" aria-label="Confirm" title="Confirm">
        <Tooltip title="Confirm">
          <CheckCircleOutlineIcon fontSize="small" />
        </Tooltip>
        <span className="import-status-label">Confirm</span>
      </ToggleButton>
      <ToggleButton value="pending" aria-label="Pending" title="Pending">
        <Tooltip title="Pending">
          <PendingIcon fontSize="small" />
        </Tooltip>
        <span className="import-status-label">Pending</span>
      </ToggleButton>
      <ToggleButton value="ignored" aria-label="Ignore" title="Ignore">
        <Tooltip title="Ignore">
          <VisibilityOffIcon fontSize="small" />
        </Tooltip>
        <span className="import-status-label">Ignore</span>
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

export function displayStatusValue(status: string) {
  if (status === "imported" || status === "mapped") return "confirmed";
  return status;
}

export function entityStatusValue(status?: string | null) {
  const value = displayStatusValue(String(status || "pending"));
  return isEditableRowStatus(value) ? value : "pending";
}

export function categoryRowParentKey(row: ImportEntityPreviewRow) {
  if (row.category_kind !== "subcategory") return "";
  return row.parent_id || row.parent_name || "";
}

export function categoryRowSortValue(row: ImportEntityPreviewRow) {
  return `${String(row.type || "")}:${String(row.name || "").toLocaleLowerCase()}:${row.row_index}`;
}

export function categoryNameTypeKey(name: string | null | undefined, type: string | null | undefined) {
  return `${String(type || "")}:${String(name || "").trim().toLowerCase()}`;
}

export function groupedCategoryEntityRows(rows: ImportEntityPreviewRow[]) {
  const sortedRows = [...rows].sort((left, right) => categoryRowSortValue(left).localeCompare(categoryRowSortValue(right)));
  const parentRows = sortedRows.filter((row) => !categoryRowParentKey(row));
  const groups: { parent: ImportEntityPreviewRow | null; children: ImportEntityPreviewRow[] }[] = [];
  // Two distinct top-level categories can share a name (e.g. "Finance" expense and "Finance"
  // income), so parents must be resolved by id/entity_id first, and only fall back to a
  // name+type key (never name alone) to avoid merging those two groups together.
  const parentById = new Map<string, ImportEntityPreviewRow>();
  const parentByEntityId = new Map<string, ImportEntityPreviewRow>();
  const parentByNameType = new Map<string, ImportEntityPreviewRow>();
  for (const row of parentRows) {
    if (row.id) parentById.set(row.id, row);
    if (row.entity_id) parentByEntityId.set(row.entity_id, row);
    parentByNameType.set(categoryNameTypeKey(row.name, row.type), row);
  }
  const childrenByParent = new Map<string, ImportEntityPreviewRow[]>();
  const orphanChildren: ImportEntityPreviewRow[] = [];
  for (const row of sortedRows) {
    const parentKey = categoryRowParentKey(row);
    if (!parentKey) continue;
    const parent =
      parentById.get(row.parent_id || "") ||
      parentByEntityId.get(row.parent_id || "") ||
      parentByNameType.get(categoryNameTypeKey(row.parent_name, row.type));
    if (!parent) {
      orphanChildren.push(row);
      continue;
    }
    const groupKey = parent.id || parent.entity_id || categoryNameTypeKey(parent.name, parent.type);
    if (!childrenByParent.has(groupKey)) childrenByParent.set(groupKey, []);
    childrenByParent.get(groupKey)!.push(row);
  }
  groups.push(...parentRows.map((parent) => ({
    parent,
    children: childrenByParent.get(parent.id || parent.entity_id || categoryNameTypeKey(parent.name, parent.type)) || [],
  })));
  for (const child of orphanChildren) {
    groups.push({ parent: null, children: [child] });
  }
  return groups;
}
